/**
 * The sound a new order makes — §9.110, FB-205.
 *
 * <p>Nothing in the manager played audio at all. Legacy plays a *"3× fast double-beep for web/pending
 * orders"* behind a *Zvuk ZAP / VYP* toggle, and a web order nobody has accepted otherwise arrives in
 * silence — the dispatcher learns about it when they next look up.
 *
 * <p><b>Two patterns, because there are two kinds of arrival.</b> `arrivalsSince` already separates an
 * order **nobody has looked at** from one already in hand, and the boards already draw them as two
 * different toasts. `urgent` is the insistent one; `gentle` is the ordinary arrival, which says
 * *something landed* without demanding the room's attention - and since §9.676 it is whichever of
 * {@link ARRIVAL_SOUND_IDS} this device chose.
 *
 * <p><b>Off by default — the owner's decision, and a divergence from legacy.</b> A tablet in a quiet
 * dining room must not start beeping because somebody deployed. It is also what the browser wants:
 * `AudioContext` stays suspended until the page has had a user gesture, so a switch somebody presses
 * *is* that gesture, where an always-on default would be a promise the browser silently breaks.
 *
 * <p><b>Synthesised, not a file</b>: no asset to load, no request that can 404 on a tablet with no
 * signal. **This said legacy oscillates two square waves and that copying it kept the sound the staff
 * knew, which was true of the urgent pattern only** (§9.676). Legacy's ordinary arrival
 * (`dispatch.tsx`) is a rising four-note sine chime, C5 E5 G5 C6 at 0.35; the port played one 660 Hz
 * square note at 0.12 in its place. That chime is *Zvonkohra* now, and a device's default.
 */

import { tt } from './i18n'

/** What kind of arrival this is. */
export type ChimeKind = 'urgent' | 'gentle'

/** One note: when it starts relative to the chime, how long it lasts, and its pitch. */
export interface ChimeTone {
  /** Seconds from the start of the chime. */
  at: number
  /** Seconds. */
  duration: number
  hertz: number
}

/**
 * The waveforms these sounds use: a square wave buzzes, a sine wave rings, and a triangle wave - §9.720's kitchen
 * call - sits between the two, brighter than a sine without a square's buzz, which is how a struck counter bell
 * sounds through a tablet's speaker.
 */
export type ChimeWave = 'sine' | 'square' | 'triangle'

/**
 * A whole sound - §9.675: one waveform at one loudness, and the notes it plays.
 *
 * <p><b>The waveform and the loudness belong to the sound, not to the player.</b> The player used to set
 * every note to a square wave at 0.12, which was right for the two sounds it had and would have played
 * legacy's ready bell - two sine notes at 0.45 - as a quiet buzz. One player serves every sound now, so
 * each one says how it is played.
 */
export interface ChimeSound {
  wave: ChimeWave
  /** The gain each note rises to, nought to one. */
  peak: number
  /** Seconds each note takes to rise to `peak`. */
  attack: number
  tones: ChimeTone[]
}

const URGENT_OFFSETS = [0, 0.22, 0.44]
const URGENT_PITCHES = [880, 1100]

/** The loudness and rise the urgent pattern and *Krátky tón* have had since §9.110. */
const ARRIVAL_PEAK = 0.12
const ARRIVAL_ATTACK = 0.01

/** C5, E5, G5 and C6 - legacy's ordinary arrival, rising. */
const LEGACY_CHIME_PITCHES = [523.25, 659.25, 783.99, 1046.5]

/**
 * The sounds a device can choose for an ordinary arrival - §9.676, owner O57 and O68.
 *
 * <p>The owner: *"zmenit zvuk na príchod novej objednávky"*, and the answer to how (#122): *a sound picker per
 * device, three or four sounds with a preview*. Four, each told apart from the others **and** from the urgent pattern
 * and the ready bell by what is played rather than by name - a test holds all six apart.
 *
 * - `zvonkohra` - legacy's own ordinary arrival, note for note. The default.
 * - `ton` - the single note AGOS played from §9.110 to §9.676, kept exactly for anybody who prefers it.
 * - `akord` - C, E and G struck together: one soft chord.
 * - `pipanie` - four short high pips, for a loud kitchen.
 */
export const ARRIVAL_SOUND_IDS = ['zvonkohra', 'ton', 'akord', 'pipanie'] as const
export type ArrivalSoundId = (typeof ARRIVAL_SOUND_IDS)[number]

/**
 * The sound a device that has chosen nothing plays - legacy's chime.
 *
 * <p>The owner asked to change the sound a new order makes, and this is the one their staff heard on legacy's board
 * for years; today's single note stays one press away as `ton`. The switch's own default is untouched: a new device is
 * still silent until somebody presses it (2026-08-12, and #122 kept it).
 */
export const DEFAULT_ARRIVAL_SOUND: ArrivalSoundId = 'zvonkohra'

/** One of the four, as it is played. Exhaustive with no `default`, so a fifth id without a sound does not compile. */
export function arrivalSound(id: ArrivalSoundId): ChimeSound {
  switch (id) {
    case 'zvonkohra':
      // `dispatch.tsx` `playNotificationSound(false)`: 0.14 s apart, each rising to 0.35 and gone by 0.35 s.
      return {
        wave: 'sine',
        peak: 0.35,
        attack: 0.02,
        tones: LEGACY_CHIME_PITCHES.map((hertz, index) => ({ at: index * 0.14, duration: 0.35, hertz })),
      }
    case 'ton':
      return {
        wave: 'square',
        peak: ARRIVAL_PEAK,
        attack: ARRIVAL_ATTACK,
        tones: [{ at: 0, duration: 0.18, hertz: 660 }],
      }
    case 'akord':
      // Three notes at 0.2 each, so the three together stay well under a full-scale signal.
      return {
        wave: 'sine',
        peak: 0.2,
        attack: 0.02,
        tones: LEGACY_CHIME_PITCHES.slice(0, 3).map((hertz) => ({ at: 0, duration: 1.2, hertz })),
      }
    case 'pipanie':
      // E6, four times: one pitch and no pairs, which is what keeps it apart from the urgent double-beeps.
      return {
        wave: 'sine',
        peak: 0.3,
        attack: 0.005,
        tones: [0, 0.12, 0.24, 0.36].map((onset) => ({ at: onset, duration: 0.07, hertz: 1318.51 })),
      }
  }
}

/** What the picker calls each sound. */
export function arrivalSoundLabel(id: ArrivalSoundId): string {
  switch (id) {
    case 'zvonkohra':
      return tt('Zvonkohra')
    case 'ton':
      return tt('Krátky tón')
    case 'akord':
      return tt('Akord')
    case 'pipanie':
      return tt('Pípanie')
  }
}

/** Where the choice lives. Per browser, beside the switch, because it is about this device's room. */
export const CHIME_CHOICE_STORAGE_KEY = 'agos_arrival_sound_choice'

/**
 * The sound this device chose, out of whatever storage holds.
 *
 * <p><b>Anything that is not one of the four is the default</b>, a missing key and a corrupted value alike.
 * Unlike the switch, the safe direction here is not silence: whether the room hears anything is the switch's
 * question, and a broken choice must not quietly answer it.
 */
export function chosenArrivalSound(stored: string | null | undefined): ArrivalSoundId {
  return ARRIVAL_SOUND_IDS.find((id) => id === stored) ?? DEFAULT_ARRIVAL_SOUND
}

/**
 * The notes of each kind of arrival.
 *
 * <p>Three fast double-beeps for the urgent case - 880 Hz then 1100 Hz, repeated, which is legacy's pattern - and the
 * device's chosen sound otherwise. **This comment used to say both were *as legacy sounds them*, and the second was
 * not** (§9.676): legacy's ordinary arrival was a four-note chime, and the port played one note in its place. The
 * pattern carries the meaning: somebody hearing it from across a kitchen should know which kind it was without looking.
 */
export function chimeTones(kind: ChimeKind, choice: ArrivalSoundId = DEFAULT_ARRIVAL_SOUND): ChimeTone[] {
  return chimeSound(kind, choice).tones
}

/**
 * The whole sound an arrival of this kind makes - its notes, waveform and loudness.
 *
 * <p>**The urgent pattern is not a choice** (§9.676): what it means is that it is unlike every other sound, so an
 * unaccepted web order sounds the same whichever sound a device picked for the rest.
 */
export function chimeSound(kind: ChimeKind, choice: ArrivalSoundId = DEFAULT_ARRIVAL_SOUND): ChimeSound {
  if (kind === 'gentle') return arrivalSound(choice)
  return {
    wave: 'square',
    peak: ARRIVAL_PEAK,
    attack: ARRIVAL_ATTACK,
    tones: URGENT_OFFSETS.flatMap((offset) =>
      URGENT_PITCHES.map((hertz, index) => ({
        at: offset + index * 0.08,
        duration: 0.07,
        hertz,
      })),
    ),
  }
}

/**
 * The kitchen's *Hotová*, heard on Otvorené stoly - §9.675, owner O56, a missed port.
 *
 * <p><b>Legacy's own ding-dong, note for note</b> (`restaurant.tsx` `playReadySound`): A5 880 Hz then D6
 * 1174.66 Hz, 0.22 s apart, sine, each rising to 0.45 over 20 ms and gone by 0.6 s. Its comment calls it
 * *"distinct from dispatch 4-note chord"*, and the owner asked for exactly that - *"zvuk aby bol iný ako keď
 * príde nová obj"*. It is not one of the arrival sounds a device can choose, so it can never come to sound like
 * the one a waiter hears for a new table.
 */
export const READY_BELL: ChimeSound = {
  wave: 'sine',
  peak: 0.45,
  attack: 0.02,
  tones: [
    { at: 0, duration: 0.6, hertz: 880 },
    { at: 0.22, duration: 0.6, hertz: 1174.66 },
  ],
}

/**
 * The kitchen calling the waiters - §9.720, owner O95, on `BACKEND.md` §10A.369.
 *
 * <p><b>A counter bell</b>: three quick strikes at E6 (1318.51 Hz), 0.14 s apart, on a triangle wave. Not the ready
 * bell's ding-dong, because the two mean different things - *an order is ready* against *come to the kitchen* - and a
 * waiter hearing one from across the room should know which without looking. Not *Pípanie* either, which shares the
 * pitch: that is four sine pips 0.12 s apart, and a test holds every sound this product makes apart by what is played.
 *
 * <p><b>The ready bell's switch governs it</b> (`READY_BELL_STORAGE_KEY`): both are the kitchen calling the waiter,
 * so one device switch silences both, and the screen still shows the call when it is muted.
 */
export const KITCHEN_CALL: ChimeSound = {
  wave: 'triangle',
  peak: 0.4,
  attack: 0.005,
  tones: [0, 0.14, 0.28].map((onset) => ({ at: onset, duration: 0.45, hertz: 1318.51 })),
}

/**
 * Where the ready bell's mute lives - §9.675. **Its own key**, so silencing the new-order chime never silences
 * the bell and the other way round: they are two different questions about two different events.
 */
export const READY_BELL_STORAGE_KEY = 'agos_ready_bell'

/**
 * Whether the ready bell rings - §9.675.
 *
 * <p><b>On unless it was switched off, which is legacy's default</b> (`rest_sound_muted === "1"` is the only
 * way it is quiet) and what *"its own mute"* means. The arrival chime's off-by-default decision of 2026-08-12 is
 * about a different sound and is not reopened: this one exists because a waiter asked to hear it, and a bell
 * that started silent after a deploy would read as the feature not working.
 */
export function readyBellEnabled(stored: string | null | undefined): boolean {
  return stored !== 'off'
}

export function storedReadyBellValue(enabled: boolean): string {
  return enabled ? 'on' : 'off'
}

/** What the bell's switch says - the state it is in, as `chimeToggleLabel` does. */
export function readyBellToggleLabel(enabled: boolean): string {
  return enabled ? tt('Zvonček z kuchyne: zapnutý') : tt('Zvonček z kuchyne: vypnutý')
}

/** How long the whole chime runs, so a caller can avoid stacking two on top of each other. */
export function chimeDurationSeconds(kind: ChimeKind): number {
  return chimeTones(kind).reduce((end, tone) => Math.max(end, tone.at + tone.duration), 0)
}

/** Where the toggle's state lives. Per browser, because it is about this device's room. */
export const CHIME_STORAGE_KEY = 'agos_arrival_sound'

/**
 * Whether the chime is on.
 *
 * <p><b>Anything that is not exactly `'on'` is off</b>, including a missing key, a corrupted value,
 * and the first visit. The safe direction for a setting about noise is silence: somebody who wants
 * sound will press the button, and somebody who does not should never have to discover why the tablet
 * is beeping.
 */
export function chimeEnabled(stored: string | null | undefined): boolean {
  return stored === 'on'
}

export function storedChimeValue(enabled: boolean): string {
  return enabled ? 'on' : 'off'
}

/** What the toggle says it will do next. */
export function chimeToggleLabel(enabled: boolean): string {
  return enabled ? 'Zvuk ZAP' : 'Zvuk VYP'
}

/**
 * What a screen that counts its arrivals does with a fresh count - FB-997, §9.677.
 *
 * <p><b>Nothing is remembered until the read has answered, and an answer of nothing is remembered as nothing.</b>
 * The kitchen and Otvorené stoly took their memory from the first render, which is the **loading** one: its list is
 * empty, so every order already waiting arrived in the next render as *new* - the kitchen toasted *Pribudla N* on
 * every fresh open, against its own comment that a cook opening the screen to six waiting orders does not need to be
 * told six arrived.
 *
 * @param previous the count remembered from the last answered read, or `null` before there was one
 * @param answered whether the screen's read has answered - the caller knows what *answered* needs
 */
export function arrivalStep(
  previous: number | null,
  current: number,
  answered: boolean,
): { baseline: number | null; arrived: number } {
  if (!answered) return { baseline: previous, arrived: 0 }
  if (previous === null) return { baseline: current, arrived: 0 }
  return { baseline: current, arrived: Math.max(0, current - previous) }
}

/**
 * Which chime an arrival deserves, if any.
 *
 * <p>Pending wins when both kinds arrived at once: the unaccepted order is the one that needs
 * somebody now, and two chimes over each other is noise rather than information.
 */
export function chimeFor(counts: { pending: number; accepted: number }): ChimeKind | null {
  if (counts.pending > 0) return 'urgent'
  if (counts.accepted > 0) return 'gentle'
  return null
}
